import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { PosProduct } from '@/features/products/types'
import type { HeldCartLine } from '@/features/pos/types'

export type CartLine = {
  /** Stable per line, so two lines of the same variant stay distinguishable. */
  key: string
  variantId: number
  productId: number
  name: string
  variantLabel: string | null
  sku: string
  /** Decimal STRING. Quantities are decimal(15,4) — 0.325 kg is a real cart line. */
  quantity: string
  /** Integer MINOR units, always (ADR-010). */
  unitPrice: number
  /** Set only when someone with pos.price_override changes it. */
  overriddenPrice: number | null
  discountType: 'percentage' | 'amount' | null
  discountValue: string
  taxRateId: number | null
  priceIncludesTax: boolean
  trackInventory: boolean
  /** Free text sent to the kitchen — "no chilli", "well done". */
  note: string | null
  /** Thumbnail for the order panel. Signed and expiring, so never persisted. */
  imageUrl: string | null
}

/**
 * Dine in or take away.
 *
 * Recorded in the sale's NOTE, not a column. `sales` gains no new required
 * column in Phase 1 (see CLAUDE.md), and this is the kind of "just one field"
 * that turns a generic POS into a restaurant POS by accident. The note is
 * also where it is actually useful — printed on the ticket the kitchen reads.
 */
export type OrderType = 'dine_in' | 'takeaway'

export type CartCustomer = { id: number; name: string; phone: string | null }

type CartState = {
  lines: CartLine[]
  customer: CartCustomer | null
  discountType: 'percentage' | 'amount' | null
  discountValue: string
  note: string
  orderType: OrderType

  addProduct: (product: PosProduct, quantity?: string) => void
  setQuantity: (key: string, quantity: string) => void
  incrementQuantity: (key: string, by: number) => void
  setLineDiscount: (key: string, type: 'percentage' | 'amount' | null, value: string) => void
  setLinePrice: (key: string, minor: number | null) => void
  setLineNote: (key: string, note: string | null) => void
  removeLine: (key: string) => void

  setCustomer: (customer: CartCustomer | null) => void
  setOrderDiscount: (type: 'percentage' | 'amount' | null, value: string) => void
  setNote: (note: string) => void
  setOrderType: (orderType: OrderType) => void
  /** Whose cart this is. Null before the first bind. */
  tenantId: number | null

  clear: () => void
  /**
   * Tie the cart to a workspace, emptying it if it belonged to another.
   *
   * The cart persists so a refreshed tab does not lose a half-rung basket.
   * That persistence had no idea WHOSE basket it was, so signing out and into
   * a different workspace in the same tab left the previous shop's lines in
   * the till — with broken thumbnails, because the image URLs were signed for
   * a tenant you are no longer in, and with variant ids the server would
   * reject at checkout.
   */
  bindTo: (tenantId: number | null) => void
  restore: (state: Pick<CartState, 'lines' | 'customer' | 'discountType' | 'discountValue' | 'note'>) => void
  restoreFromHeld: (cart: HeldCartLine[], customer: CartCustomer | null) => void
}

/**
 * The cart.
 *
 * Deliberately NOT React Query state. A cart is not a cached copy of something
 * on the server — it does not exist there until it is charged, and treating it
 * as server state means every keystroke is a candidate for a background
 * refetch that would wipe what the cashier is doing.
 *
 * Persisted to sessionStorage so an accidental refresh, or a tablet that
 * reloads the tab under memory pressure, does not lose a half-built order in
 * front of a waiting customer. sessionStorage rather than local: a cart is
 * per-tab and per-session, and finding yesterday's abandoned order on Monday
 * morning is worse than losing it.
 *
 * Every amount here is an integer in minor units. There is no float anywhere
 * in this file, and an ESLint rule keeps parseFloat and toFixed out of
 * features/pos entirely.
 */
export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      lines: [],
      customer: null,
      discountType: null,
      discountValue: '',
      note: '',
      orderType: 'dine_in',
      tenantId: null,

      addProduct: (product, quantity = '1') =>
        set((state) => {
          // Scanning the same barcode twice increments rather than adding a
          // second identical row — but only when the existing line has no
          // per-line discount or override, because merging those would
          // silently reprice what the cashier already decided.
          const existing = state.lines.find(
            (line) =>
              line.variantId === product.variant_id &&
              line.discountType === null &&
              line.overriddenPrice === null,
          )

          if (existing) {
            return {
              lines: state.lines.map((line) =>
                line.key === existing.key
                  ? { ...line, quantity: addDecimal(line.quantity, quantity) }
                  : line,
              ),
            }
          }

          return {
            lines: [
              ...state.lines,
              {
                key: crypto.randomUUID(),
                variantId: product.variant_id,
                productId: product.product_id,
                name: product.name,
                variantLabel: product.variant_label,
                sku: product.sku,
                quantity,
                unitPrice: product.price,
                overriddenPrice: null,
                discountType: null,
                discountValue: '',
                taxRateId: product.tax_rate_id,
                priceIncludesTax: product.price_includes_tax,
                trackInventory: product.track_inventory,
                note: null,
                imageUrl: product.image_url,
              },
            ],
          }
        }),

      setQuantity: (key, quantity) =>
        set((state) => ({
          lines: state.lines.map((line) => (line.key === key ? { ...line, quantity } : line)),
        })),

      /*
       * Never below zero.
       *
       * The order panel's minus button called straight through to here with
       * no guard of its own, so holding it took a line to -12 at zero rupees.
       * The menu tile had always removed the line at one; the panel had not,
       * and the two had drifted.
       *
       * The clamp lives HERE rather than at the call site because it is an
       * invariant of a cart, not a detail of one button — a negative quantity
       * is a negative line total, which is a refund wearing a sale's clothes.
       * The panel separately removes the line at one, so nothing is left
       * sitting at zero.
       */
      incrementQuantity: (key, by) =>
        set((state) => ({
          lines: state.lines.map((line) => {
            if (line.key !== key) return line

            const next = addDecimal(line.quantity, String(by))

            return { ...line, quantity: toUnits(next) < 0 ? '0' : next }
          }),
        })),

      setLineDiscount: (key, type, value) =>
        set((state) => ({
          lines: state.lines.map((line) =>
            line.key === key ? { ...line, discountType: type, discountValue: value } : line,
          ),
        })),

      setLinePrice: (key, minor) =>
        set((state) => ({
          lines: state.lines.map((line) =>
            line.key === key ? { ...line, overriddenPrice: minor } : line,
          ),
        })),

      setLineNote: (key, note) =>
        set((state) => ({
          lines: state.lines.map((line) => (line.key === key ? { ...line, note } : line)),
        })),

      removeLine: (key) =>
        set((state) => ({ lines: state.lines.filter((line) => line.key !== key) })),

      setCustomer: (customer) => set({ customer }),

      setOrderDiscount: (discountType, discountValue) => set({ discountType, discountValue }),

      setNote: (note) => set({ note }),

      setOrderType: (orderType) => set({ orderType }),

      clear: () =>
        set({
          lines: [],
          customer: null,
          discountType: null,
          discountValue: '',
          note: '',
          // Deliberately NOT reset: a counter runs dine-in all lunchtime, and
          // flipping back to the default after every order would have the
          // cashier re-selecting it forty times a service.
        }),

      bindTo: (tenantId) =>
        set((state) => {
          // First sight of a workspace, or the same one again: keep the cart.
          if (state.tenantId === null || state.tenantId === tenantId) {
            return { tenantId };
          }

          // A different workspace. Everything in the basket belongs to the
          // previous one — its variant ids would be rejected at checkout and
          // its thumbnails are signed for a tenant this session cannot read.
          return {
            tenantId,
            lines: [],
            customer: null,
            discountType: null,
            discountValue: '',
            note: '',
          };
        }),

      restore: (state) => set(state),

      /**
       * Replay a parked basket.
       *
       * REPLACES rather than merges. Merging two tables' orders is a mistake
       * nobody can unpick from a receipt afterwards, and the dialog says so
       * before it happens.
       *
       * New keys are minted: the held rows carry no client key, and reusing
       * an index would collide with whatever is already open.
       */
      restoreFromHeld: (cart, customer) =>
        set({
          customer,
          discountType: null,
          discountValue: '',
          note: '',
          lines: cart.map((line) => ({
            key: crypto.randomUUID(),
            variantId: line.product_variant_id,
            productId: 0,
            name: line.name,
            variantLabel: line.variant_label ?? null,
            sku: '',
            quantity: line.quantity,
            unitPrice: line.unit_price,
            overriddenPrice: null,
            discountType: line.discount_type ?? null,
            discountValue: line.discount_value ?? '',
            taxRateId: line.tax_rate_id ?? null,
            priceIncludesTax: line.price_includes_tax ?? true,
            trackInventory: line.track_inventory ?? false,
            note: line.note ?? null,
            imageUrl: line.image_url ?? null,
          })),
        }),
    }),
    {
      name: 'voltra.cart',
      storage: createJSONStorage(() => sessionStorage),

      // Thumbnails are signed, expiring URLs. Persisting one means a cart
      // restored an hour later renders broken images — so it is dropped and
      // the row falls back to its initial. The cart itself survives; only the
      // picture does not.
      partialize: (state) => ({
        ...state,
        lines: state.lines.map((line) => ({ ...line, imageUrl: null })),
      }),
    },
  ),
)

/**
 * Add two decimal quantity strings without touching a float.
 *
 * Quantities are decimal(15,4), so this works in ten-thousandths — the same
 * scale the server uses. `0.1 + 0.2` in JavaScript is 0.30000000000000004,
 * and a cart that shows that has lost the customer's confidence before the
 * total is even wrong.
 */
export function addDecimal(a: string, b: string): string {
  const scaled = toUnits(a) + toUnits(b)
  return fromUnits(scaled)
}

const SCALE = 10_000

export function toUnits(decimal: string): number {
  const [whole = '0', fraction = ''] = decimal.trim().split('.')
  const padded = (fraction + '0000').slice(0, 4)
  const sign = whole.trimStart().startsWith('-') ? -1 : 1

  return sign * (Math.abs(Number(whole)) * SCALE + Number(padded))
}

export function fromUnits(units: number): string {
  const sign = units < 0 ? '-' : ''
  const absolute = Math.abs(units)

  return `${sign}${Math.floor(absolute / SCALE)}.${String(absolute % SCALE).padStart(4, '0')}`
}

/** Trailing zeros stripped, for display. "3.0000" reads as "3". */
export function forHumans(decimal: string): string {
  return decimal.includes('.') ? decimal.replace(/\.?0+$/, '') || '0' : decimal
}
